import type { CreditCardType } from "credit-card-type/src/types";

import {
  CardEligibile,
  CardNumberOptions,
  CreditCardTypeCardBrandId,
} from "cc-tools/types";

import type { CreditCardNetwork } from "chameleon/lib/components/display-cards/display-cards";

export const luhn = (pan: string): boolean => {
  const total = pan
    .split("")
    .reverse()
    .reduce((total, str, idx) => {
      let num = parseInt(str, 10);

      if (idx % 2 !== 0) {
        num *= 2;
        if (num > 9) {
          num = (num % 10) + 1;
        }
      }
      return (total += num);
    }, 0);
  return total % 10 === 0;
};

export const unformatCardInput = (inputValue: string | unknown): string => {
  return typeof inputValue === "string"
    ? // remove formating spacing
      String(inputValue).replace(/-|\s/g, "")
    : "";
};

export const notProperStringOfNumbers = (inputValue: string): boolean => {
  return !inputValue || !/^\d*$/.test(inputValue);
};

export const notProperCreditCardType = (possibleTypes: CreditCardType[]) => {
  return possibleTypes.length === 0 || possibleTypes.length > 1;
};

export const notAllowedCreditCardNetwork = (
  possibleTypes: CreditCardType[],
  allowedTypes: CreditCardNetwork[]
) => {
  // Skip is allowed  if allowed types are empty
  if (!allowedTypes.length) {
    return true;
  }

  for (const possibility of possibleTypes) {
    for (const allowed of allowedTypes) {
      if (possibility.type === allowed) {
        return false;
      }
      // Library doesn't use the term AMEX
      if (possibility.type === "american-express" && allowed === "amex") {
        return false;
      }
      // Library doesn't use the term unionPay
      if (possibility.type === "unionpay" && allowed === "unionPay") {
        return false;
      }
      // Library doesn't use the term dinersClub
      if (possibility.type === "diners-club" && allowed === "dinersClub") {
        return false;
      }
    }
  }

  return true;
};

type NotProperOptionalLength = {
  options: CardNumberOptions;
  rawCardValue: string;
};

export const notProperOptionalLength = ({
  options,
  rawCardValue,
}: NotProperOptionalLength): boolean => {
  if (options.maxLength) {
    return rawCardValue.length > options.maxLength;
  }
  return false;
};

type CheckLuhn = {
  rawCardValue: string;
  cardType: CreditCardType;
  options: CardNumberOptions;
};

export const checkLuhn = ({
  rawCardValue,
  cardType,
  options,
}: CheckLuhn): CardEligibile => {
  // I think I'd want to remove Union Pay until its asked for by product
  const isEligible = !options.luhnValidateUnionPay && luhn(rawCardValue);

  for (let i = 0; i < cardType.lengths.length; i++) {
    if (cardType.lengths[i] === rawCardValue.length) {
      // CC Number passes Luhn and we have identified the Card type
      return { isEligible, cardType: cardType };
    }
  }

  // fails luhn, but a cardtype is recognized, but has an incomplete CC number
  // Edge case of Merchant Override of maxLength (Not sure how much I like this)
  // TODO LUCIANO What is the purpose of this?
  if (options.maxLength) {
    const minOfMaxLength = Math.min(options.maxLength, ...cardType.lengths);
    cardType.lengths = [minOfMaxLength];
  }
  return { isEligible: false, cardType: cardType };
};

export const addGaps = (string = "", gaps: number[], gapChar = " ") => {
  const offsets = [0].concat(gaps).concat([string.length]);

  return offsets
    .map((end, index) => {
      if (index === 0) return "";
      const start = offsets[index - 1];
      return string.substr(start, end - start);
    })
    .filter((part) => part !== "")
    .join(gapChar);
};

export const removeNonNumber = (string = ""): string =>
  string.replace(/[^\d]/g, "");

export const prettifyUICardNumber = (value: string, gaps: number[]) => {
  return addGaps(removeNonNumber(value), gaps);
};

export const padExpirationMonthValue = (value: string) => {
  if (value.length === 1 && !isNaN(Number(value))) {
    return `0${value}`;
  }

  return value;
};

export const prettifyExpirationValue = (value: string) => {
  if (!value) {
    return "";
  }
  const number = value.replace(/\D+/g, "");
  if (number.length === 0) {
    return "";
  } else if (number.length === 1) {
    if (!["0", "1"].includes(number)) {
      return `0${value} / `;
    }
    return value;
  } else if (number.length === 2) {
    if (number[0] === "0" && Number(number) > 0) {
      return value;
    } else if (number[0] === "1" && Number(number) < 13) {
      return value;
    } else if (Number(number) > 0) {
      return `0${number[0]} / ${number[1]}`;
    } else {
      return number[0];
    }
  } else if (number.length === 3) {
    if (number[0] === "0" && Number(number.substring(0, 2)) > 0) {
      return `${number.substring(0, 2)} / ${number[2]}`;
    } else if (number[0] === "1" && Number(number.substring(0, 2)) < 13) {
      return `${number.substring(0, 2)} / ${number[2]}`;
    } else {
      return `0${number[0]} / ${number.substring(1, 3)}`;
    }
  } else {
    if (number[0] === "0" && Number(number.substring(0, 3)) > 0) {
      return `${number.substring(0, 2)} / ${number.substring(2, 4)}`;
    } else if (number[0] === "1" && Number(number.substring(0, 2)) < 13) {
      return `${number.substring(0, 2)} / ${number.substring(2, 4)}`;
    } else {
      return `${number.substring(0, 2)} / ${number.substring(2, 4)}`;
    }
  }
};

export const padTo16LengthUsingZeros = (input: string): string => {
  if (input.length === 0 || input.length >= 16) {
    return input;
  }
  return input ? input.padEnd(16, "0") : "";
};

export const isArrayOfIntegersEqual = (
  arr1: number[],
  arr2: number[]
): boolean => {
  if (arr1.length !== arr2.length) {
    return false;
  }
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) {
      return false;
    }
  }
  return true;
};

export const niceTypesToCreditCardTypeCardBrandId: CreditCardTypeCardBrandId = {
  americanExpress: "american-express",
  discover: "discover",
  mastercard: "mastercard",
  maestro: "maestro",
  visa: "visa",
  jcb: "jcb",
  unionPay: "unionpay",
  dinersClub: "diners-club",
  cartesBancaires: "cartes-bancaires",
};

export const mapCardNetworkEnumMembersToCreditCardTypeCardBrandId: CreditCardTypeCardBrandId =
  {
    UNSPECIFIED: "",
    VISA: "visa",
    MASTERCARD: "mastercard",
    AMEX: "american-express",
    DISCOVER: "discover",
    JCB: "jcb",
    MAESTRO: "maestro",
    DINERS: "diners-club",
    // CHASE_VISA_CREDIT,
    // CHINA_UNION_PAY
    CARTES_BANCAIRES: "cartes-bancaires",
  };

export function getCardNetworkStringFromNiceType(
  niceType: string
): string | undefined {
  const creditCardTypeCardBrandId =
    niceTypesToCreditCardTypeCardBrandId[niceType];
  const cardNetworkString = Object.keys(
    mapCardNetworkEnumMembersToCreditCardTypeCardBrandId
  ).find(
    (key) =>
      mapCardNetworkEnumMembersToCreditCardTypeCardBrandId[
        key as keyof typeof mapCardNetworkEnumMembersToCreditCardTypeCardBrandId
      ] === creditCardTypeCardBrandId
  );
  return cardNetworkString;
}
